import { formatInTimeZone } from "date-fns-tz";
import { Field, Form, Formik } from "formik";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { shallowEqual, useSelector } from "react-redux";
import Select from "react-select";
import { ErrorContainer, Input } from "../../../common/control/InputField";
import DateRangeComp from "../../../common/DateRangeComp";
import { PromotionDialogSchema } from "../../schema/PromotionDialogSchema";

const initValues = {
  promotitle: "",
  applytoall: "false",
  promotype: "gameextra",
  freecreditjson: {
    freecredittype: "freebonus",
    freedemandnum: "0",
  },
  free: "",
  freelimit: "",
  minremove: "",
  givetype: "",
  startdate: new Date(),
  enddate: new Date(),
  topupmin: "",
  topupmax: "",
  daytype: "",
  turnover: "",
  claimlimit: "",
  companylist: [],
};

export const PromotionForm = ({
  formikRef,
  promotionId,
  saveValues,
  isEditable,
}) => {
  const intl = useIntl();
  const [isRerender, setIsRerender] = useState(0);
  const hideConfigPromo = ["viewonly"];
  const promotionState = useSelector(
    ({ promotion }) => promotion,
    shallowEqual
  );

  useEffect(() => {
    if (promotionState.promotionUI) {
      const promoState = promotionState.promotionUI;
      if (!promotionId) {
        formikRef.current.setValues({
          ...initValues,
          type: promoState.promotionUI_PromoType[0].code,
          givetype: promoState.promotionUI_GiveType[0].code,
          daytype: promoState.promotionUI_DayType[0].code,
        });
      } else {
        const currentPromo = promotionState.promotionLists?.promotionLists.find(
          (promotion) => promotion.promotionid === promotionId
        );
        setIsRerender((prev) => (prev += 1));
        formikRef.current.setValues({
          ...initValues,
          promotitle: currentPromo?.promotiontitle ?? "",
          promotype: currentPromo?.promotiontype ?? "",
          freecreditjson: promoState?.freeCreditJson ?? {
            freecredittype:
              promoState?.freeCreditJson?.freecredittype ?? "freebonus",
            freedemandnum: promoState?.freeCreditJson?.freedemandnum ?? "0",
          },
          claimlimit: promoState.claimlimit,
          enddate: new Date(promoState.enddate),
          free: promoState.free,
          startdate: new Date(promoState.startdate),
          topupmax: promoState.topupmax,
          topupmin: promoState.topupmin,
          turnover: promoState.turnover,
          freelimit: promoState.freelimit ?? "",
          minremove: promoState.minremove,
          type:
            promoState.promotionUI_PromoType.find(
              (promo) => promo.selected === "true"
            )?.code ?? promoState.promotionUI_PromoType[0].code,
          givetype:
            promoState.promotionUI_GiveType.find(
              (promo) => promo.selected === "true"
            )?.code ?? promoState.promotionUI_GiveType[0].code,
          daytype:
            promoState.promotionUI_DayType.find(
              (promo) => promo.selected === "true"
            )?.code ?? promoState.promotionUI_DayType[0].code,
          companylist: promoState.promotionUI_Company.filter(
            (promo) => promo.selected === "true"
          ),
        });
      }
    }
  }, [promotionState.promotionUI, promotionId]);

  const groupOptions = () => {
    if (promotionState.promotionUI) {
      const rawOption = [...promotionState.promotionUI.promotionUI_Company];
      const grouped = {};

      rawOption.forEach((item) => {
        if (!grouped[item.companytype]) {
          grouped[item.companytype] = [];
        }
        grouped[item.companytype].push({
          label: item.description,
          value: item,
          isDisabled: false,
        });
      });

      const sortedGroupKeys = Object.keys(grouped).sort();

      return sortedGroupKeys?.map((type) => ({
        label: type,
        options: grouped[type],
      }));
    }
    return [];
  };

  return (
    <>
      {promotionState.promotionUI?.code === "0" && (
        <Formik
          innerRef={formikRef}
          validationSchema={PromotionDialogSchema}
          initialValues={initValues}
          enableReinitialize={true}
          onSubmit={(values) => {
            const timeZone = "Asia/Kuala_Lumpur";
            const queryParams = {
              startdate: !hideConfigPromo.includes(values.promotype)
                ? formatInTimeZone(values.startdate, timeZone, "yyyy-MM-dd")
                : "",
              enddate:
                !hideConfigPromo.includes(values.promotype) &&
                values.promotype !== "freecredit"
                  ? formatInTimeZone(values.enddate, timeZone, "yyyy-MM-dd")
                  : "",
            };
            saveValues({
              promotitle: values.promotitle,
              applytoall: "false",
              promotype: values.promotype,
              free: !hideConfigPromo.includes(values.promotype)
                ? values.free
                : "",
              givetype: !hideConfigPromo.includes(values.promotype)
                ? values.givetype
                : "",
              ...queryParams,
              freelimit:
                values.promotype === "gameextra" &&
                values.givetype === "percent"
                  ? values.freelimit
                  : "",
              freecreditjson:
                values.promotype === "freecredit" ? values.freecreditjson : "",
              topupmin: values.promotype === "gameextra" ? values.topupmin : "",
              topupmax: values.promotype === "gameextra" ? values.topupmax : "",
              minremove: !hideConfigPromo.includes(values.promotype)
                ? values.minremove
                : "",
              daytype: !hideConfigPromo.includes(values.promotype)
                ? values.daytype
                : "",
              turnover: !hideConfigPromo.includes(values.promotype)
                ? values.turnover
                : "",
              claimlimit: !hideConfigPromo.includes(values.promotype)
                ? values.claimlimit
                : "",
              companylist: !hideConfigPromo.includes(values.promotype)
                ? values.companylist
                : "",
            });
          }}
        >
          {({
            values,
            handleSubmit,
            setFieldValue,
            touched,
            errors,
            setFieldError,
          }) => (
            <Form>
              <div className="mb-2">
                <label className="mb-2 text-input-label">
                  {intl.formatMessage({ id: "title" })}
                </label>

                <Field
                  name="promotitle"
                  component={Input}
                  placeholder={intl.formatMessage({ id: "title" })}
                  autoComplete="off"
                  type="text"
                  inputClass="form-control"
                  disabled={!!promotionId || !isEditable}
                  isAlphanumericOnly={false}
                />
              </div>
              <div className="mb-2">
                <label className="mb-2 text-input-label">
                  {intl.formatMessage({ id: "type" })}
                </label>
                <select
                  className="form-select"
                  name="promotype"
                  onChange={(e) => {
                    setFieldValue("promotype", e.target.value);
                    if (e.target.value === "freecredit")
                      setFieldValue("givetype", "bonus");
                  }}
                  value={values.promotype}
                  disabled={!!promotionId || !isEditable}
                >
                  {promotionState.promotionUI?.promotionUI_PromoType?.map(
                    (promo) => (
                      <option value={promo.code} key={promo.code}>
                        {promo.description}
                      </option>
                    )
                  )}
                </select>
              </div>
              {values.promotype === "freecredit" && (
                <div
                  className="mb-2 p-2 rounded"
                  style={{ border: "1px dashed blue" }}
                >
                  <label className="mb-2 text-input-label">
                    {intl.formatMessage({ id: "freeCreditObtainReward" })}
                  </label>
                  <div className="row">
                    <div className="col-lg-4">
                      <select
                        className="form-select"
                        name="freecreditjson.freecredittype"
                        value={values.freecreditjson.freecredittype}
                        onChange={(e) => {
                          setFieldValue(
                            "freecreditjson.freecredittype",
                            e.target.value
                          );
                          if (e.target.value === "freebonus")
                            setFieldValue("freecreditjson.freecredittype", "0");
                        }}
                        disabled={!!promotionId || !isEditable}
                      >
                        {promotionState.promotionUI?.promotionUI_FreeCreditType?.map(
                          (promo) => (
                            <option value={promo.code} key={promo.code}>
                              {promo.description}
                            </option>
                          )
                        )}
                      </select>
                    </div>
                    <div className="col-lg-8">
                      <Field
                        name="freecreditjson.freedemandnum"
                        component={Input}
                        placeholder={intl.formatMessage({ id: "obtainReward" })}
                        autoComplete="off"
                        type="number"
                        inputClass="form-control"
                        disabled={
                          values.freecreditjson.freecredittype ===
                            "freebonus" || !isEditable
                        }
                      />
                    </div>
                  </div>
                </div>
              )}
              {!hideConfigPromo.includes(values.promotype) && (
                <>
                  <div className="mb-2">
                    <label className="mb-2 text-input-label">
                      {intl.formatMessage({ id: "giveType" })}
                    </label>
                    <div className="row">
                      <div className="col-lg-4">
                        <select
                          className="form-select"
                          name="givetype"
                          value={values.givetype}
                          onChange={(e) =>
                            setFieldValue("givetype", e.target.value)
                          }
                          disabled={
                            values.promotype === "freecredit" || !isEditable
                          }
                        >
                          {promotionState.promotionUI?.promotionUI_GiveType?.map(
                            (promo) => (
                              <option value={promo.code} key={promo.code}>
                                {promo.description}
                              </option>
                            )
                          )}
                        </select>
                      </div>
                      <div className="col-lg-8">
                        <Field
                          name="free"
                          component={Input}
                          placeholder={intl.formatMessage({ id: "free" })}
                          autoComplete="off"
                          type="number"
                          inputClass="form-control"
                          disabled={!isEditable}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-lg-4 mb-2">
                      <label className="mb-2 text-input-label">
                        {intl.formatMessage({ id: "turnover" })}
                      </label>

                      <Field
                        name="turnover"
                        component={Input}
                        placeholder={intl.formatMessage({ id: "turnover" })}
                        autoComplete="off"
                        type="number"
                        inputClass="form-control"
                        disabled={!isEditable}
                      />
                    </div>
                    {values.givetype === "disabled" && (
                      <div className="col-lg-8 mb-2">
                        <label className="mb-2 text-input-label">
                          {intl.formatMessage({ id: "freeLimit" })}
                        </label>

                        <Field
                          name="freelimit"
                          component={Input}
                          placeholder={intl.formatMessage({
                            id: "freeLimit",
                          })}
                          autoComplete="off"
                          type="number"
                          inputClass="form-control"
                          disabled={!isEditable}
                        />
                      </div>
                    )}
                  </div>
                  <div className="row">
                    <div className="col-lg-4 mb-2">
                      <label className="mb-2 text-input-label">
                        {intl.formatMessage({ id: "minRemove" })}
                      </label>

                      <Field
                        name="minremove"
                        component={Input}
                        placeholder={intl.formatMessage({ id: "minRemove" })}
                        autoComplete="off"
                        type="number"
                        inputClass="form-control"
                        disabled={!isEditable}
                      />
                    </div>
                  </div>

                  <div className="mb-2">
                    <label className="mb-2 text-input-label">
                      {intl.formatMessage({ id: "promotionPeriod" })}
                    </label>
                    <DateRangeComp
                      initialRange={[
                        {
                          startDate: values.startdate,
                          endDate: values.enddate,
                          key: "selection",
                        },
                      ]}
                      onDateChange={(startDate, endDate) => {
                        setFieldValue("startdate", startDate);
                        setFieldValue("enddate", endDate);
                      }}
                      className="form-control"
                      isRemoveMaxDate={true}
                      isRerender={isRerender}
                      disabled={!isEditable}
                    />
                  </div>
                  {values.promotype === "gameextra" && (
                    <div>
                      <div className="row">
                        <div className="col-lg-6 mb-2">
                          <label className="mb-2 text-input-label">
                            {intl.formatMessage({ id: "topupMin" })}
                          </label>
                          <Field
                            name="topupmin"
                            component={Input}
                            placeholder={intl.formatMessage({
                              id: "topupMin",
                            })}
                            autoComplete="off"
                            type="number"
                            inputClass="form-control"
                            disabled={!isEditable}
                          />
                        </div>
                        <div className="col-lg-6 mb-2">
                          <label className="mb-2 text-input-label">
                            {intl.formatMessage({ id: "topupMax" })}
                          </label>
                          <Field
                            name="topupmax"
                            component={Input}
                            placeholder={intl.formatMessage({
                              id: "topupMax",
                            })}
                            autoComplete="off"
                            type="number"
                            inputClass="form-control"
                            disabled={!isEditable}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="mb-2">
                    <label className="mb-2 text-input-label">
                      {intl.formatMessage({ id: "dayType" })}
                    </label>

                    <select
                      className="form-select"
                      name="daytype"
                      onChange={(e) => setFieldValue("daytype", e.target.value)}
                      value={values.daytype}
                      disabled={!isEditable}
                    >
                      {promotionState.promotionUI?.promotionUI_DayType?.map(
                        (promo) => (
                          <option value={promo.code} key={promo.code}>
                            {promo.description}
                          </option>
                        )
                      )}
                    </select>
                  </div>
                  <div className="mb-2">
                    <label className="mb-2 text-input-label">
                      {intl.formatMessage({ id: "claimLimit" })}
                    </label>

                    <Field
                      name="claimlimit"
                      component={Input}
                      placeholder={intl.formatMessage({ id: "claimLimit" })}
                      autoComplete="off"
                      type="number"
                      inputClass="form-control"
                      disabled={!isEditable}
                    />
                  </div>
                  <div className="mb-2">
                    <label className="mb-2 text-input-label">
                      {intl.formatMessage({ id: "company" })}
                    </label>
                    <Select
                      value={values.companylist?.map((promo) => ({
                        value: promo,
                        label: promo.description,
                      }))}
                      isMulti
                      closeMenuOnSelect={false}
                      isDisabled={!isEditable}
                      name="companylist"
                      options={groupOptions()}
                      className={`basic-multi-select ${
                        touched.companylist && errors.companylist
                          ? "invalid"
                          : ""
                      }`}
                      classNamePrefix="select"
                      onChange={(selectedList) => {
                        setFieldValue(
                          "companylist",
                          selectedList?.map((selected) => selected.value)
                        );
                      }}
                    />
                    <ErrorContainer
                      isShow={touched.companylist && errors.companylist}
                      errorMsg={errors.companylist}
                    />
                  </div>
                </>
              )}
            </Form>
          )}
        </Formik>
      )}
    </>
  );
};
